import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      ref={menuRef}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || menuOpen || !isHomePage ? 'bg-[#1A1A1A] shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 flex items-center justify-between h-[64px] md:h-[72px]">
        {/* Logo */}
        <Link to="/" className="flex items-center h-full py-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/783dd8c3d2d1f38f9648f46144ac17f1/cb31b4c1fc494d309ebf77b43b96df0f.png"
            alt="BEST Building Supplies"
            className="h-9 md:h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex items-center gap-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <Link
            to="/products"
            className={`text-[14px] font-semibold tracking-wide hover:text-[#F2C612] transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              location.pathname === '/products' ? 'text-[#F2C612]' : 'text-white'
            }`}
          >
            Products
          </Link>
          <Link
            to="/about"
            className={`text-[14px] font-semibold tracking-wide hover:text-[#F2C612] transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              location.pathname === '/about' ? 'text-[#F2C612]' : 'text-white'
            }`}
          >
            About Us
          </Link>
          {isHomePage && (
            <>
              <button
                onClick={() => scrollTo('three-tier')}
                className="text-white text-[14px] font-semibold tracking-wide hover:text-[#F2C612] transition-colors duration-200 whitespace-nowrap cursor-pointer bg-transparent border-none"
              >
                Our Ranges
              </button>
              <button
                onClick={() => scrollTo('featured-projects')}
                className="text-white text-[14px] font-semibold tracking-wide hover:text-[#F2C612] transition-colors duration-200 whitespace-nowrap cursor-pointer bg-transparent border-none"
              >
                Projects
              </button>
              <button
                onClick={() => scrollTo('learn-teaser')}
                className="text-white text-[14px] font-semibold tracking-wide hover:text-[#F2C612] transition-colors duration-200 whitespace-nowrap cursor-pointer bg-transparent border-none"
              >
                Learn
              </button>
            </>
          )}
          <Link
            to="/contact"
            className="bg-[#F2C612] text-black px-5 py-2 rounded text-[14px] font-bold hover:bg-yellow-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Contact Us
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-[5px] cursor-pointer bg-transparent border-none p-2 min-h-[44px] min-w-[44px] items-center justify-center z-50"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-[2px] bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
          <span className={`block w-6 h-[2px] bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`block w-6 h-[2px] bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
        </button>
      </div>

      {/* Mobile Menu — full-width dropdown */}
      {menuOpen && (
        <div
          className="md:hidden w-full bg-[#1A1A1A] border-t border-white/10 z-50"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          <div className="px-4 py-5 flex flex-col gap-1">
            <Link
              to="/products"
              onClick={() => setMenuOpen(false)}
              className={`text-[15px] font-semibold py-3 px-2 border-b border-white/10 hover:text-[#F2C612] transition-colors duration-200 cursor-pointer ${
                location.pathname === '/products' ? 'text-[#F2C612]' : 'text-white'
              }`}
            >
              Products
            </Link>
            <Link
              to="/about"
              onClick={() => setMenuOpen(false)}
              className={`text-[15px] font-semibold py-3 px-2 border-b border-white/10 hover:text-[#F2C612] transition-colors duration-200 cursor-pointer ${
                location.pathname === '/about' ? 'text-[#F2C612]' : 'text-white'
              }`}
            >
              About Us
            </Link>
            {isHomePage && (
              <>
                <button
                  onClick={() => scrollTo('three-tier')}
                  className="text-white text-[15px] font-semibold py-3 px-2 border-b border-white/10 text-left hover:text-[#F2C612] transition-colors duration-200 cursor-pointer bg-transparent w-full"
                  style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
                >
                  Our Ranges
                </button>
                <button
                  onClick={() => scrollTo('featured-projects')}
                  className="text-white text-[15px] font-semibold py-3 px-2 border-b border-white/10 text-left hover:text-[#F2C612] transition-colors duration-200 cursor-pointer bg-transparent w-full"
                  style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
                >
                  Projects
                </button>
                <button
                  onClick={() => scrollTo('learn-teaser')}
                  className="text-white text-[15px] font-semibold py-3 px-2 border-b border-white/10 text-left hover:text-[#F2C612] transition-colors duration-200 cursor-pointer bg-transparent w-full"
                  style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
                >
                  Learn
                </button>
              </>
            )}
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="mt-3 bg-[#F2C612] text-black px-5 py-3 rounded text-[15px] font-bold hover:bg-yellow-400 transition-colors duration-200 whitespace-nowrap cursor-pointer w-full text-center min-h-[48px] flex items-center justify-center"
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
